import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import moment from "moment";
import { i18n } from "../../translate/i18n";
import { formatBytes } from "../../helpers/formatBytes";

// Atalho para as chaves desta tela (companyStorage.*).
const t = (key, options) => i18n.t(`companyStorage.${key}`, options);

const CATEGORIES = [
  "messages",
  "profilePics",
  "quickMessages",
  "internalChat",
  "campaigns",
  "others",
];

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
  },
  tile: {
    height: "100%",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
  },
  tileLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  infoIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    cursor: "help",
  },
  tileValue: {
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },
  ranking: {
    marginTop: theme.spacing(2),
  },
  rankingRow: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 30%) 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0.5),
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  rankingName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.action.selected,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    minWidth: 2,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
  rankingValue: {
    minWidth: 72,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
}));

// Conteúdo do tooltip de uma empresa: quantidade de arquivos e bytes por categoria.
export const StorageBreakdown = ({ item }) => (
  <div>
    <div>{t("files", { count: item.fileCount })}</div>
    {CATEGORIES.filter((key) => (item.breakdown || {})[key] > 0).map((key) => (
      <div key={key}>
        {t(`categories.${key}`)}: {formatBytes(item.breakdown[key])}
      </div>
    ))}
  </div>
);

const StatTile = ({ classes, label, hint, value, detail }) => (
  <div className={classes.tile}>
    <div className={classes.tileLabel}>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      {hint && (
        <Tooltip title={hint}>
          <InfoOutlinedIcon className={classes.infoIcon} />
        </Tooltip>
      )}
    </div>
    <Typography variant="h5" className={classes.tileValue}>
      {value}
    </Typography>
    {detail && (
      <Typography variant="caption" color="textSecondary">
        {detail}
      </Typography>
    )}
  </div>
);

/**
 * Resumo do espaço em disco (somente super usuário): total em disco, atribuído
 * às empresas e sem vínculo, com o botão "Recalcular". Com `limit`, mostra
 * também o ranking das empresas que mais ocupam espaço.
 * `storage` é o retorno de useCompanyStorage.
 */
const CompanyStorageSummary = ({ storage, limit, showSubtitle = false }) => {
  const classes = useStyles();
  const { data, loading, requesting, refresh } = storage;
  const running = !!(data && data.running);
  const totals = data && data.totals;
  const companies = (data && data.companies) || [];
  const ranking = limit ? companies.slice(0, limit) : [];
  const maxBytes = ranking.length ? ranking[0].totalBytes || 1 : 1;

  const renderStatus = () => {
    if (running) {
      return (
        <Tooltip title={t("runningHint")}>
          <Typography variant="body2" color="textSecondary">
            {t("running")}
          </Typography>
        </Tooltip>
      );
    }
    if (data && data.computedAt) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t("updatedAt", {
            date: moment(data.computedAt).format("DD/MM/YYYY HH:mm"),
          })}
        </Typography>
      );
    }
    if (data) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t("neverCalculated")}
        </Typography>
      );
    }
    return null;
  };

  return (
    <div>
      <div className={classes.header}>
        <div>
          <Typography component="h2" variant="h6" color="primary">
            {t("title")}
          </Typography>
          {showSubtitle && (
            <Typography variant="body2" color="textSecondary">
              {t("subtitle")}
            </Typography>
          )}
        </div>
        <div className={classes.headerActions}>
          {renderStatus()}
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={refresh}
            disabled={running || requesting || !data}
            startIcon={
              running || requesting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
          >
            {t("refresh")}
          </Button>
        </div>
      </div>

      {data && data.lastError && !running && (
        <Typography variant="body2" className={classes.error}>
          {t("lastError", { error: data.lastError.error || "-" })}
        </Typography>
      )}

      {!data && loading && <CircularProgress size={24} />}

      {totals && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <StatTile
              classes={classes}
              label={t("diskTotal")}
              value={formatBytes(totals.diskBytes)}
              detail={t("files", { count: totals.diskFiles })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatTile
              classes={classes}
              label={t("attributed")}
              hint={t("attributedHint")}
              value={formatBytes(totals.attributedBytes)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatTile
              classes={classes}
              label={t("unattributed")}
              hint={t("unattributedHint")}
              value={formatBytes(totals.unattributedBytes)}
              detail={t("files", { count: totals.unattributedFiles })}
            />
          </Grid>
        </Grid>
      )}

      {totals && ranking.length > 0 && (
        <div className={classes.ranking}>
          <Typography variant="subtitle2" gutterBottom>
            {t("ranking")}
          </Typography>
          {ranking.map((item) => (
            <Tooltip
              key={item.companyId}
              title={<StorageBreakdown item={item} />}
              placement="top-start"
            >
              <div className={classes.rankingRow}>
                <Typography variant="body2" className={classes.rankingName}>
                  {item.companyName}
                </Typography>
                <div className={classes.barTrack}>
                  <div
                    className={classes.barFill}
                    style={{ width: `${(item.totalBytes / maxBytes) * 100}%` }}
                  />
                </div>
                <Typography variant="body2" className={classes.rankingValue}>
                  {formatBytes(item.totalBytes)}
                </Typography>
              </div>
            </Tooltip>
          ))}
          <Typography variant="caption" color="textSecondary">
            {t("rankingShowing", {
              shown: ranking.length,
              total: companies.length,
            })}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default CompanyStorageSummary;
